/** Operations (C07-001..005): paid-order rescue, AI spend caps + kill switch, feature flags, audit cost dashboard, model allowlist. */
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import type { Flag } from '@qm/contracts/flags';
import { useSurface } from '../kit/context';
import { newIdempotencyKey } from '../kit/http';
import { useAction, useResource } from '../kit/resource';
import { Badge, Checkbox, DataTable, ErrorNote, Field, Loadable, Money, Notice, ReasonForm, Screen, Stat, Tabs, When } from '../kit/ui';
import type { AllowlistEntry, CostRow } from '../api';
import { allowlistChange, canGrantComplimentary, canSupport, componentsSum, costTotals, isFinance, isSuperAdmin, overdueRescue, rescueSort } from '../lib/logic';
import { useAdminApi } from './layout';

export function RescuePage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const list = useResource('rescue', api.rescueOrders);
  const retry = useAction(api.retryRescue);
  return (
    <Screen title={t('portal.admin.rescue.title')} subtitle={t('portal.admin.rescue.subtitle')} testId="rescue">
      <Loadable resource={list}>
        {(rows) => <DataTable rows={rescueSort(rows)} rowKey={(r) => r.orderId} empty={t('portal.admin.rescue.none')} columns={[
          { key: 'o', header: t('portal.admin.rescue.order'), render: (r) => <code>{r.orderId}</code> },
          { key: 'c', header: t('portal.admin.rescue.category'), render: (r) => <Badge tone="bad" testId={`rescue-${r.category}`}>{t(`portal.admin.rescue.cat.${r.category}`)}</Badge> },
          { key: 'a', header: t('portal.admin.rescue.age'), render: (r) => <span data-overdue={overdueRescue(r)}>{t('portal.admin.rescue.minutes', { n: r.ageMinutes })}</span> },
          { key: 'al', header: t('portal.admin.rescue.alert'), render: (r) => (r.alertedAt ? <When iso={r.alertedAt} /> : '-') },
          { key: 'h', header: t('portal.admin.rescue.history'), narrow: 'hide', render: (r) => r.retries.map((x) => `${x.outcome} (${x.by})`).join('; ') || '-' },
          { key: 'r', header: '', render: (r) => (canSupport(roles) ? <Button onClick={async () => { if (await retry.run(r.orderId)) list.reload(); }} disabled={retry.pending}>{t('portal.admin.rescue.retry')}</Button> : null) },
        ]} />}
      </Loadable>
      <Text tone="muted">{t('portal.admin.rescue.retryNote')}</Text>
      <ErrorNote error={retry.error} />
      {canGrantComplimentary(roles) ? <GrantComplimentary /> : <Text tone="muted" data-testid="grant-hidden">{t('portal.admin.rescue.grantSuperAdmin')}</Text>}
    </Screen>
  );
}

/** Super Admin only (the server also refuses Support with 403). */
function GrantComplimentary() {
  const { t } = useSurface();
  const api = useAdminApi();
  const [tenantId, setTenantId] = useState('');
  const [requestId] = useState(newIdempotencyKey);
  const grant = useAction((reason: string) => api.grantComplimentary({ tenantId: tenantId.trim(), requestId, sku: 'audit_pro', reason, expiresInDays: 30 }));
  return (
    <Panel data-testid="grant-complimentary">
      <Heading level={2}>{t('portal.admin.rescue.grant')}</Heading>
      <Field label={t('portal.admin.impersonation.tenant')}>{(id) => <input id={id} value={tenantId} onChange={(e) => setTenantId(e.target.value)} />}</Field>
      <ReasonForm label={t('portal.kit.reason')} submitLabel={t('portal.admin.rescue.grant')} pending={grant.pending} onSubmit={(r) => (tenantId.trim() ? grant.run(r) : undefined)} />
      {grant.result ? <Notice tone="good">{t('portal.admin.rescue.granted')}</Notice> : null}
      <ErrorNote error={grant.error} />
    </Panel>
  );
}

export function AiControlsPage() {
  const { t, roles } = useSurface();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') ?? 'caps';
  if (!isSuperAdmin(roles) && !isFinance(roles)) return <Screen title={t('portal.admin.ai.title')}><Notice>{t('portal.admin.superAdminOnly')}</Notice></Screen>;
  return (
    <Screen title={t('portal.admin.ai.title')} testId="ai-controls">
      <Tabs label={t('portal.admin.ai.title')} active={tab} onSelect={(v) => setSp({ tab: v })} tabs={[{ id: 'caps', label: t('portal.admin.ai.tab.caps') }, { id: 'allowlist', label: t('portal.admin.ai.tab.allowlist') }]} />
      {tab === 'allowlist' ? <Allowlist /> : <><KillSwitch /><Caps /></>}
    </Screen>
  );
}

function KillSwitch() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const ks = useResource('ai:kill', api.killSwitch);
  const set = useAction((engaged: boolean, reason: string) => api.setKillSwitch(engaged, reason));
  return (
    <Loadable resource={ks}>
      {(k) => (
        <Panel data-testid="kill-switch">
          <Heading level={2}>{t('portal.admin.ai.killSwitch')} <Badge tone={k.engaged ? 'bad' : 'good'} testId={`kill-${k.engaged ? 'on' : 'off'}`}>{k.engaged ? t('portal.admin.ai.engaged') : t('portal.admin.ai.off')}</Badge></Heading>
          <Text tone="muted">{t('portal.admin.ai.killNote')}</Text>
          {isSuperAdmin(roles) || isFinance(roles) ? (
            <ReasonForm danger={!k.engaged} label={t('portal.kit.reason')} submitLabel={k.engaged ? t('portal.admin.ai.release') : t('portal.admin.ai.engage')} pending={set.pending} onSubmit={async (r) => { if (await set.run(!k.engaged, r)) ks.reload(); }} />
          ) : null}
          <ErrorNote error={set.error} />
        </Panel>
      )}
    </Loadable>
  );
}

function Caps() {
  const { t } = useSurface();
  const api = useAdminApi();
  const [tenantId, setTenantId] = useState('');
  const [lookup, setLookup] = useState('');
  const budget = useResource(`ai:budget:${lookup}`, () => (lookup ? api.aiBudget(lookup) : Promise.resolve(null)));
  const [cap, setCap] = useState('');
  const save = useAction((reason: string) => api.setAiCap({ tenantId: lookup, capMinor: Math.round(Number(cap) * 100), currency: 'USD', reason }));
  return (
    <Panel data-testid="ai-caps">
      <Heading level={2}>{t('portal.admin.ai.caps')}</Heading>
      <form className="qx-toolbar" onSubmit={(e) => { e.preventDefault(); setLookup(tenantId.trim()); }}>
        <Field label={t('portal.admin.impersonation.tenant')}>{(id) => <input id={id} value={tenantId} onChange={(e) => setTenantId(e.target.value)} />}</Field>
        <Button type="submit">{t('portal.admin.search')}</Button>
      </form>
      <Loadable resource={budget}>
        {(b) => (b ? (
          <>
            <div className="qx-grid">
              <Stat label={t('portal.admin.ai.cap')} value={<Money minor={b.capMinor} currency={b.currency} />} />
              <Stat label={t('portal.admin.ai.reserved')} value={<Money minor={b.reservedMinor} currency={b.currency} />} />
              <Stat testId="ai-spent" label={t('portal.admin.ai.spent')} value={<Money minor={b.spentMinor} currency={b.currency} />} />
            </div>
            <Field label={t('portal.admin.ai.newCap')}>{(id) => <input id={id} type="number" min="0" step="0.01" value={cap} onChange={(e) => setCap(e.target.value)} />}</Field>
            <ReasonForm label={t('portal.kit.reason')} submitLabel={t('portal.admin.save')} pending={save.pending} onSubmit={async (r) => { if (cap !== '' && (await save.run(r))) budget.reload(); }} />
            <Text tone="muted">{t('portal.admin.ai.capNote')}</Text>
          </>
        ) : null)}
      </Loadable>
      <ErrorNote error={save.error} />
    </Panel>
  );
}

function Allowlist() {
  const { roles } = useSurface();
  const api = useAdminApi();
  const list = useResource('ai:allowlist', async () => { const r = await api.allowlist(); return Array.isArray(r) ? r : r.entries; });
  return <Loadable resource={list}>{(rows) => <AllowlistEditor initial={rows} editable={isSuperAdmin(roles)} onSaved={list.reload} />}</Loadable>;
}

export function AllowlistEditor({ initial, editable, onSaved }: { initial: AllowlistEntry[]; editable: boolean; onSaved: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const [rows, setRows] = useState(initial);
  const save = useAction((reason: string) => api.saveAllowlist(rows, reason));
  return (
    <Panel data-testid="allowlist">
      <Text tone="muted">{t('portal.admin.ai.allowlistNote')}</Text>
      {rows.map((r) => <Checkbox key={`${r.provider}:${r.model}`} name={`${r.provider}:${r.model}`} label={`${r.provider} / ${r.model}`} checked={r.enabled} disabled={!editable} onChange={(v) => setRows((xs) => allowlistChange(xs, r.provider, r.model, v))} />)}
      {editable ? <ReasonForm label={t('portal.kit.reason')} submitLabel={t('portal.admin.save')} pending={save.pending} onSubmit={async (r) => { if (await save.run(r)) onSaved(); }} /> : null}
      <ErrorNote error={save.error} />
    </Panel>
  );
}

export function FlagsPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const flags = useResource('flags', api.flags);
  const [open, setOpen] = useState<string | null>(null);
  if (!isSuperAdmin(roles)) return <Screen title={t('portal.admin.flags.title')}><Notice>{t('portal.admin.superAdminOnly')}</Notice></Screen>;
  return (
    <Screen title={t('portal.admin.flags.title')} subtitle={t('portal.admin.flags.subtitle')} testId="flags">
      <Loadable resource={flags}>
        {(rows) => <DataTable<Flag> rows={rows} rowKey={(f) => f.key} empty={t('portal.kit.empty')} onRowClick={(f) => setOpen(f.key)} columns={[
          { key: 'k', header: t('portal.admin.flags.key'), render: (f) => <button type="button" className="qx-link" aria-expanded={open === f.key} onClick={(e) => { e.stopPropagation(); setOpen(f.key); }} data-testid="flag-open"><code>{f.key}</code></button> },
          { key: 'o', header: t('portal.admin.flags.owner'), render: (f) => f.owner },
          { key: 'd', header: t('portal.admin.flags.default'), render: (f) => (f.defaultEnabled ? t('portal.admin.flags.on') : t('portal.admin.flags.off')) },
          { key: 'ks', header: t('portal.admin.flags.kill'), render: (f) => (f.killSwitch ? <Badge tone="bad" testId="flag-killed">{t('portal.admin.flags.killed')}</Badge> : '-') },
          { key: 'e', header: t('portal.admin.flags.expires'), narrow: 'hide', render: (f) => <When iso={f.expiresAt} /> },
        ]} />}
      </Loadable>
      {open ? <FlagPanel flagKey={open} onChanged={flags.reload} /> : null}
    </Screen>
  );
}

function FlagPanel({ flagKey, onChanged }: { flagKey: string; onChanged: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const overrides = useResource(`flag:${flagKey}`, () => api.flagOverrides(flagKey));
  const [tenants, setTenants] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const ids = tenants.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
  const enable = useAction((reason: string) => api.enableForTenants(flagKey, ids, true, reason, expiresAt ? new Date(expiresAt).toISOString() : null));
  const kill = useAction((reason: string) => api.killFlag(flagKey, reason));
  const done = async (p: Promise<unknown>) => { if (await p) { overrides.reload(); onChanged(); } };
  return (
    <Panel data-testid="flag-panel">
      <Heading level={2}><code>{flagKey}</code></Heading>
      <Field label={t('portal.admin.flags.tenants')} hint={t('portal.admin.flags.tenantsHint')}>{(id) => <textarea id={id} value={tenants} onChange={(e) => setTenants(e.target.value)} />}</Field>
      <Field label={t('portal.admin.flags.expires')}>{(id) => <input id={id} type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />}</Field>
      <ReasonForm testId="flag-enable" label={t('portal.kit.reason')} submitLabel={t('portal.admin.flags.enableFor', { n: ids.length })} pending={enable.pending} onSubmit={(r) => (ids.length ? done(enable.run(r)) : undefined)} />
      <ReasonForm testId="flag-kill" danger label={t('portal.kit.reason')} submitLabel={t('portal.admin.flags.killNow')} pending={kill.pending} onSubmit={(r) => done(kill.run(r))} />
      <Text tone="muted">{t('portal.admin.flags.serverNote')}</Text>
      <Loadable resource={overrides}>
        {(rows) => <DataTable rows={rows} rowKey={(o) => o.id} empty={t('portal.kit.empty')} columns={[
          { key: 't', header: t('portal.admin.impersonation.tenant'), render: (o) => o.tenantId },
          { key: 'e', header: t('portal.admin.flags.state'), render: (o) => (o.enabled ? t('portal.admin.flags.on') : t('portal.admin.flags.off')) },
          { key: 'r', header: t('portal.kit.reason'), render: (o) => o.reason },
          { key: 'x', header: t('portal.admin.flags.expires'), render: (o) => <When iso={o.expiresAt} /> },
        ]} />}
      </Loadable>
      <ErrorNote error={enable.error ?? kill.error} />
    </Panel>
  );
}

/** Per-audit cost vs price from provider records; estimates are marked as estimates (C07-004). Finance only. */
export function CostsPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const rows = useResource('costs', async () => { const r = await api.auditCosts({}); return Array.isArray(r) ? r : r.rows; });
  if (!isFinance(roles)) return <Screen title={t('portal.admin.costs.title')}><Notice testId="finance-only">{t('portal.admin.financeOnly')}</Notice></Screen>;
  return (
    <Screen title={t('portal.admin.costs.title')} testId="costs">
      <Loadable resource={rows}>
        {(list) => {
          const tot = costTotals(list);
          const cur = list[0]?.currency ?? 'USD';
          return (
            <>
              <div className="qx-grid">
                <Stat label={t('portal.admin.costs.audits')} value={list.length} />
                <Stat testId="cost-per-audit" label={t('portal.admin.costs.perAudit')} value={<Money minor={tot.perAudit} currency={cur} />} />
                <Stat label={t('portal.admin.costs.margin')} value={<Money minor={tot.margin} currency={cur} />} />
                <Stat label={t('portal.admin.costs.estimates')} value={tot.estimated} />
              </div>
              <DataTable<CostRow> rows={list} rowKey={(r) => r.auditId} empty={t('portal.kit.empty')} columns={[
                { key: 'a', header: t('portal.admin.costs.audit'), render: (r) => <code>{r.auditId}</code> },
                { key: 'p', header: t('portal.admin.billing.price'), render: (r) => <Money minor={r.priceMinor} currency={r.currency} /> },
                { key: 'c', header: t('portal.admin.costs.cost'), render: (r) => <Money minor={r.totalCostMinor} currency={r.currency} /> },
                { key: 'b', header: t('portal.admin.costs.breakdown'), narrow: 'hide', render: (r) => t('portal.admin.costs.parts', { tokens: r.costs.tokens, browser: r.costs.browser, api: r.costs.api, render: r.costs.render, email: r.costs.email }) },
                { key: 'm', header: t('portal.admin.costs.margin'), render: (r) => <Money minor={r.marginMinor} currency={r.currency} /> },
                { key: 'e', header: t('portal.admin.costs.source'), render: (r) => (r.estimated ? <Badge tone="warn" testId="cost-estimate">{t('portal.admin.costs.estimate')}</Badge> : componentsSum(r) === r.totalCostMinor ? <Badge tone="good" testId="cost-actual">{t('portal.admin.costs.actual')}</Badge> : <Badge tone="bad">{t('portal.admin.costs.unreconciled')}</Badge>) },
              ]} />
            </>
          );
        }}
      </Loadable>
    </Screen>
  );
}
